import { useState, useCallback, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

const ShimmerLoader = () => (
  <div style={{ position:'absolute', inset:0, zIndex:2, background:'#fff',
    display:'flex', alignItems:'center', justifyContent:'center',
    boxShadow:'0 10px 25px -5px rgba(0,0,0,0.15)' }}>
    <div style={{ width:'70%' }}>
      {[55,100,80,65].map((w,i) => (
        <div key={i} style={{ height:i===0?20:12, borderRadius:4, background:'#e5e7eb',
          marginBottom:12, width:`${w}%`,
          animation:`pulse 1.5s ease-in-out ${i*0.15}s infinite` }} />
      ))}
    </div>
    <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>
  </div>
);

// options: pdf.js getDocument options (keep the object stable between renders)
export const PdfViewer = ({ url, options, showNavigation = false }) => {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [pageNaturalWidth, setPageNaturalWidth] = useState(null);
  const [pageNaturalHeight, setPageNaturalHeight] = useState(null);
  const [inputPage, setInputPage] = useState('1');
  const [errorMsg, setErrorMsg] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
      setContainerHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handlePageLoadSuccess = useCallback((pageProxy) => {
    const vp = pageProxy.getViewport({ scale: 1 });
    setPageNaturalWidth(vp.width);
    setPageNaturalHeight(vp.height);
    setIsPageLoading(false);
  }, []);

  const onLoadSuccess = useCallback(({ numPages }) => {
    setErrorMsg(null);
    setNumPages(numPages);
    setPage(1);
    setInputPage('1');
  }, []);

  const goTo = (n) => {
    const clamped = Math.min(Math.max(1, n), numPages ?? 1);
    if (clamped !== page) setIsPageLoading(true);
    setPage(clamped);
    setInputPage(String(clamped));
  };

  const handlePageInputBlur = () => {
    const n = parseInt(inputPage, 10);
    if (!isNaN(n)) goTo(n);
    else setInputPage(String(page));
  };

  const baseWidth = (() => {
    if (!containerWidth) return undefined;
    const maxW = containerWidth - 32;
    if (!pageNaturalWidth || !pageNaturalHeight || !containerHeight) {
      return Math.floor(maxW);
    }
    const maxH = containerHeight - 32;
    const fitByHeight = maxH * (pageNaturalWidth / pageNaturalHeight);
    return Math.floor(Math.min(maxW, fitByHeight));
  })();

  const pageWidth = baseWidth ? Math.floor(baseWidth * scale) : undefined;

  return (
    <div className="flex flex-col w-full h-full bg-gray-200">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 text-white text-sm shrink-0">
        {/* Page navigation — only when showNavigation */}
        {showNavigation ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => goTo(page - 1)}
              disabled={page <= 1}
              className="px-2 py-1 rounded hover:bg-gray-600 disabled:opacity-40 text-lg leading-none"
            >
              ‹
            </button>
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={inputPage}
                onChange={(e) => setInputPage(e.target.value)}
                onBlur={handlePageInputBlur}
                onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                className="w-10 text-center bg-gray-700 rounded px-1 py-0.5 text-white"
              />
              <span className="text-gray-400">/ {numPages ?? '...'}</span>
            </div>
            <button
              onClick={() => goTo(page + 1)}
              disabled={page >= (numPages ?? 1)}
              className="px-2 py-1 rounded hover:bg-gray-600 disabled:opacity-40 text-lg leading-none"
            >
              ›
            </button>
          </div>
        ) : (
          <div />
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={() => setScale(s => Math.max(MIN_ZOOM, +(s - ZOOM_STEP).toFixed(2)))}
            disabled={scale <= MIN_ZOOM}
            className="px-2 py-1 rounded hover:bg-gray-600 disabled:opacity-40 text-lg leading-none"
          >
            −
          </button>
          <span className="w-14 text-center">{Math.round(scale * 100)}%</span>
          <button
            onClick={() => setScale(s => Math.min(MAX_ZOOM, +(s + ZOOM_STEP).toFixed(2)))}
            disabled={scale >= MAX_ZOOM}
            className="px-2 py-1 rounded hover:bg-gray-600 disabled:opacity-40 text-lg leading-none"
          >
            +
          </button>
          <button
            onClick={() => setScale(1)}
            className="px-2 py-1 rounded hover:bg-gray-600 text-xs text-gray-300"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-200">
        <div
          style={{
            minWidth: baseWidth ? `${Math.floor(baseWidth * scale) + 32}px` : '100%',
            width: 'max-content',
            minHeight: '100%',
          }}
          className="flex justify-center py-4 mx-auto"
        >
          <Document
            file={url}
            options={options}
            onLoadSuccess={onLoadSuccess}
            onLoadError={(err) => setErrorMsg(err?.message ?? 'Unknown error')}
            loading={<ShimmerLoader />}
            error={
              <div className="flex flex-col items-center justify-center h-64 gap-2 text-red-500 px-4 text-center">
                <span className="font-medium">Failed to load PDF.</span>
                {errorMsg && <span className="text-xs text-red-400">{errorMsg}</span>}
              </div>
            }
          >
            <div style={{ position: 'relative' }}>
              {isPageLoading && <ShimmerLoader />}

              {showNavigation ? (
                // Single-page navigation mode
                <Page
                  key={`page_${page}`}
                  pageNumber={page}
                  width={pageWidth}
                  onLoadSuccess={handlePageLoadSuccess}
                  renderTextLayer
                  renderAnnotationLayer
                  className="shadow-lg"
                  loading={null}
                />
              ) : (
                // Scroll mode — all pages
                Array.from(new Array(numPages), (_, index) => (
                  <div key={`page_${index + 1}`} style={{ marginBottom: '10px' }}>
                    <Page
                      pageNumber={index + 1}
                      width={pageWidth}
                      onLoadSuccess={handlePageSuccessOnce(index, handlePageLoadSuccess)}
                      renderTextLayer
                      renderAnnotationLayer
                      className="shadow-lg"
                      loading={null}
                    />
                  </div>
                ))
              )}
            </div>
          </Document>
        </div>
      </div>
    </div>
  );
};

// Helper: only run onLoadSuccess once (avoid re-setting natural size)
const handlePageSuccessOnce = (index, fn) => (proxy) => {
  if (index === 0) fn(proxy);
};